import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Drawer, Button } from "antd";
import { MenuOutlined, CloseOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";

import { fetchMenu } from "../../api/newsService";
import { slugify } from "../Sdata";
import { NetBlueLogo, NetLogo } from "../../assets";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";

const HEADER_FONT_STYLE = { fontFamily: "var(--font-header)" };
const HEADER_FONT_WHITE_STYLE = { ...HEADER_FONT_STYLE, color: "#ffffff" };

function buildMenuTree(data) {
  const map = {};
  const roots = [];

  data.forEach((item) => {
    map[item.id] = { ...item, children: [] };
  });

  data.forEach((item) => {
    if (item.parentId === null || item.parentId === undefined) {
      roots.push(map[item.id]);
    } else {
      map[item.parentId]?.children.push(map[item.id]);
    }
  });

  return roots;
}

function MenuItem({ item }) {
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li className="relative group/lv1">
      {hasChildren ? (
        <button
          type="button"
          className="flex items-center gap-1 px-2 xl:px-3 py-6 font-medium text-white transition-all duration-300 group-hover/lv1:-translate-y-0.5 group-hover/lv1:text-white"
          style={HEADER_FONT_WHITE_STYLE}
        >
          <span className="relative text-[17px] xl:text-[19px] text-white">
            {item.name}
            <span className="absolute left-1/2 top-[calc(100%+8px)] h-[3px] w-0 -translate-x-1/2 rounded-full bg-white transition-all duration-300 group-hover/lv1:w-full" />
          </span>
          <ChevronDown size={20} className="mt-1 transition-transform duration-300 group-hover/lv1:rotate-180" style={{ color: "#ffffff" }} />
        </button>
      ) : (
        <Link
          to={`/${slugify(item.name)}`}
          className="group block w-full px-2 xl:px-3 py-6  font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:text-white"
          style={HEADER_FONT_WHITE_STYLE}
        >
          <span className="relative text-[17px] xl:text-[19px]" style={{ color: "#ffffff" }}>
            {item.name}
            <span className="absolute left-1/2 top-[calc(100%+8px)] h-[3px] w-0 -translate-x-1/2 rounded-full bg-white transition-all duration-300 group-hover:w-full" />
          </span>
        </Link>
      )}

      {/* dropdown level 1 */}
      {hasChildren && (
        <ul className="absolute left-0 top-full z-50 mt-[-4px] min-w-[220px] rounded-lg border bg-white shadow-[0_20px_45px_rgba(15,23,42,0.16)] opacity-0 invisible translate-y-3 pointer-events-none transition-all duration-300 ease-out group-hover/lv1:visible group-hover/lv1:translate-y-0 group-hover/lv1:opacity-100 group-hover/lv1:pointer-events-auto">
          {item.children.map((child) => (
            <SubMenu key={child.id} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function SubMenu({ item }) {
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li className="relative group/lv2">
      {hasChildren ? (
        <button
          type="button"
          className="w-full rounded-md text-[15px] xl:text-[16px] font-medium text-gray-600 text-left px-4 py-2 hover:bg-blue-50 hover:text-blue-600 flex justify-between items-center"
          style={HEADER_FONT_STYLE}
        >
          {item.name}
          <ChevronRight size={20} className="text-gray-500" />
        </button>
      ) : (
        <Link
          to={`/${slugify(item.name)}`}
          className="block rounded-md text-[15px] xl:text-[16px] font-medium text-gray-600 px-4 py-2 hover:bg-blue-50 hover:text-blue-600"
          style={HEADER_FONT_STYLE}
        >
          {item.name}
        </Link>
      )}

      {hasChildren && (
        <ul className="absolute left-full top-0 min-w-[300px] rounded-lg border bg-white shadow-[0_18px_40px_rgba(15,23,42,0.14)] opacity-0 invisible translate-x-3 pointer-events-none transition-all duration-300 ease-out group-hover/lv2:visible group-hover/lv2:translate-x-0 group-hover/lv2:opacity-100 group-hover/lv2:pointer-events-auto">
          {item.children.map((child) => (
            <SubMenu key={child.id} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function MobileMenuNode({ item, level = 0, openMap, setOpenMap, onNavigate }) {
  const hasChildren = item.children && item.children.length > 0;
  const isOpen = !!openMap[item.id];

  const paddingLeft = 12 + level * 14;

  const toggle = () => {
    setOpenMap((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  };

  return (
    <div>
      <div
        className="flex items-center justify-between py-2"
        style={{ paddingLeft, paddingRight: 12 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={toggle}
            className="flex-1 text-left text-[15px] font-medium text-gray-800 hover:text-[#0072CE]"
            style={HEADER_FONT_STYLE}
          >
            {item.name}
          </button>
        ) : (
          <Link
            to={`/${slugify(item.name)}`}
            onClick={onNavigate}
            className="flex-1 text-[15px] font-medium text-gray-800 hover:text-[#0072CE]"
            style={{ ...HEADER_FONT_STYLE, paddingLeft: 0 }}
          >
            {item.name}
          </Link>
        )}

        {hasChildren && (
          <button
            type="button"
            onClick={toggle}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
            aria-label="toggle"
          >
            {isOpen ? <DownOutlined /> : <RightOutlined />}
          </button>
        )}
      </div>

      {hasChildren && isOpen && (
        <div className="pb-1">
          {item.children.map((child) => (
            <MobileMenuNode
              key={child.id}
              item={child}
              level={level + 1}
              openMap={openMap}
              setOpenMap={setOpenMap}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Head() {
  const [menu, setMenu] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMap, setOpenMap] = useState({});

  const noiBoUrl = process.env.REACT_APP_NOI_BO;
  const location = useLocation();

  useEffect(() => {
    const getMenu = async () => {
      const res = await fetchMenu();
      if (res?.data?.data) setMenu(buildMenuTree(res.data.data));
    };
    getMenu();
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const onNavigate = () => setDrawerOpen(false);

  const drawerTitle = useMemo(
    () => (
      <div className="flex items-center justify-between">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2">
          <img src={NetBlueLogo} alt="VNPT" className="h-10" />
        </Link>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setDrawerOpen(false)}
        />
      </div>
    ),
    []
  );

  return (
    <header
      className="w-full bg-transparent"
      style={HEADER_FONT_STYLE}
    >
      <div className="fixed left-0 top-0 z-50 flex h-16 w-full items-center justify-between bg-[#095FC0] px-4 shadow-md md:h-[72px] md:px-8 2xl:px-48 lg:h-20">
        {/* logo */}
        <Link to="/" className="flex items-center">
          <img src={NetLogo} alt="VNPT" className="h-10 xl:h-12" />
        </Link>

        <div className="flex gap-4">
          {/* desktop menu */}
          <nav className="hidden lg:block">
            <ul className="flex items-center gap-2">
              {menu.map((item) => (
                <MenuItem key={item.id} item={item} />
              ))}
            </ul>
          </nav>

          {/* right side */}
          <div className="flex items-center gap-3">
            <a
              href={noiBoUrl}
              className="hidden lg:flex items-center bg-[#F1531F] text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90"
            >
              <span className="mr-2 text-white text-[16px] xl:text-[18px]">Trang nội bộ</span>
              <ArrowRight size={20} />
            </a>
            <Button
              className="lg:hidden !text-white hover:!text-white"
              type="text"
              icon={<MenuOutlined style={{ fontSize: 20, color: "#ffffff" }} />}
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
            />
          </div>
        </div>
      </div>

      {/* Drawer mobile */}
      <Drawer
        title={drawerTitle}
        placement="right"
        onClose={() => setDrawerOpen(false)}
        closeIcon={null}
        open={drawerOpen}
        width={420}
        styles={{
          wrapper: { maxWidth: "90vw" },
          body: { padding: 0, fontFamily: "var(--font-header)" },
          header: { padding: "12px 12px", fontFamily: "var(--font-header)" },
        }}
      >
        <div className="px-2">
          <div className="py-2 border-b">
            <a
              href={noiBoUrl}
              className="inline-flex w-full justify-center bg-[#F1531F] text-white px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90 hover:text-white"
            >
              Trang nội bộ
            </a>
          </div>

          <div className="py-2">
            {menu.map((item) => (
              <MobileMenuNode
                key={item.id}
                item={item}
                openMap={openMap}
                setOpenMap={setOpenMap}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      </Drawer>
      <div className="h-16 md:h-[72px] lg:h-20" />
    </header>
  );
}
